import React from "react";
import { Link } from "react-router-dom";
import { Award, ShieldCheck, GraduationCap, Briefcase, MapPin, Users } from "lucide-react";
import { Container, Section, SectionTitle, Eyebrow, PageHero, Card, CTABand } from "@/components/ui-bits/Primitives";
import { IMAGES, COMPANY } from "@/lib/site-data";

export default function About() {
    return (
        <div data-testid="about-page">
            <PageHero
                kicker="About M-Class"
                title="Built in North Texas. Engineered for the nation."
                lead="A veteran-owned EV charging infrastructure company committed to rural and underserved communities — and to long-term partnerships that outlast the build."
                image={IMAGES.cityNight}
            />

            <Section>
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-7 space-y-6 text-slate-300 leading-relaxed text-base md:text-lg">
                            <Eyebrow>Our Story</Eyebrow>
                            <p>M-Class EV Charging was founded in North Texas with a simple conviction: the transition to electric transportation must be built — not just announced. While the headlines focus on vehicle launches and megafactories, the daily reliability of our energy transition depends on a different layer of infrastructure: the chargers themselves.</p>
                            <p>We exist to expand access to that infrastructure — from the dense corridors of the DFW Metroplex to the small towns and rural counties too often left out of the first wave of deployment. Our team navigates the entire lifecycle: grants and procurement, engineering and permitting, utility coordination, construction, commissioning, and 24/7 operations.</p>
                            <p>As a veteran-owned business, we bring a discipline of mission, accountability, and long-horizon thinking to every site we develop — and we partner with property owners, municipalities, fleets, and utilities who expect the same.</p>
                        </div>
                        <div className="lg:col-span-5 grid grid-cols-2 gap-4">
                            {[
                                { icon: ShieldCheck, k: "Veteran-Owned", v: "Mission-driven discipline" },
                                { icon: MapPin, k: "Frisco, TX", v: "Headquartered in North Texas" },
                                { icon: Users, k: "Communities First", v: "Rural & underserved focus" },
                                { icon: Briefcase, k: "Turnkey", v: "End-to-end delivery" },
                            ].map((b) => {
                                const I = b.icon;
                                return (
                                    <Card key={b.k} hover={false} className="!p-6">
                                        <I className="w-6 h-6 text-electric-400 mb-4" strokeWidth={1.5} />
                                        <div className="font-heading text-white text-sm mb-1">{b.k}</div>
                                        <div className="text-xs text-slate-400">{b.v}</div>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </Container>
            </Section>

            {/* FOUNDER */}
            <Section className="border-t border-white/5">
                <Container>
                    <div className="max-w-4xl">
                        <Eyebrow className="mb-4">Leadership</Eyebrow>
                        <h2 className="font-heading font-medium text-white text-4xl md:text-5xl tracking-tight leading-[1.05]">Omari Ross</h2>
                        <div className="mt-2 text-electric-400 text-sm uppercase tracking-[0.22em]">Founder & CEO</div>
                        <div className="mt-8 space-y-5 text-slate-300 leading-relaxed text-base md:text-lg">
                            <p>Omari Ross founded M-Class EV Charging to bridge the gap between cutting-edge transportation technology and the communities that need it most. His focus is squarely on electric vehicle charging systems (EVCS) — site selection, utility coordination, deployment, and long-term operation of public and fleet charging infrastructure.</p>
                            <p>His academic foundation in material science and commercialization translates directly into the practical engineering and procurement decisions required to deploy reliable EVCS at scale.</p>
                        </div>
                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Card hover={false} className="!p-5 flex items-start gap-3">
                                <GraduationCap className="w-5 h-5 text-electric-400 mt-0.5" />
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Education</div>
                                    <div className="text-sm text-white mt-1">M.S. Biology · Material Science & Commercialization</div>
                                    <div className="text-xs text-slate-500 mt-1">University of Houston · Texas State University</div>
                                </div>
                            </Card>
                            <Card hover={false} className="!p-5 flex items-start gap-3">
                                <Award className="w-5 h-5 text-electric-400 mt-0.5" />
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Focus</div>
                                    <div className="text-sm text-white mt-1">EVCS — Site Development, Deployment & Operations</div>
                                </div>
                            </Card>
                        </div>
                    </div>

                    {/* COO */}
                    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Card hover={false} className="md:col-span-1">
                            <div className="text-[11px] uppercase tracking-[0.2em] text-electric-400 mb-3">Operations</div>
                            <div className="font-heading text-2xl text-white">Robert Ross</div>
                            <div className="text-sm text-electric-400 uppercase tracking-[0.18em] mt-1">Head of Operations</div>
                            <p className="mt-5 text-sm text-slate-400 leading-relaxed">Leads day-to-day operations, project delivery, and field execution across active and developing M-Class sites.</p>
                        </Card>
                        <div className="md:col-span-2">
                            <Eyebrow className="mb-4">Our Approach</Eyebrow>
                            <h3 className="font-heading text-2xl md:text-3xl text-white tracking-tight leading-tight">We don't sell hardware. We deliver outcomes — and we stay accountable for them.</h3>
                            <p className="mt-4 text-slate-400 leading-relaxed">Our partners measure us by uptime, utilization, and the long-term value our sites create. We own the operations side of every deployment — not because it's profitable, but because it's the only way to guarantee infrastructure performance over a 10-year horizon.</p>
                        </div>
                    </div>
                </Container>
            </Section>

            <CTABand title="Partner with a veteran-owned EV charging operator." subtitle="From grants to operations, we run the full stack so you don't have to." />
        </div>
    );
}
